import React, { useEffect, useState, useRef } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import OSelect from '../../../../Components/OElements/OSelect';
import OInput from '../../../../Components/OElements/OInput';
import { useFormikContext } from 'formik';

// Helpers
import {
	getEstablishmentList,
	getPersonList,
} from '../../../../helpers/establishment/backend_helper';
import { getWarehouseList } from '../../../../helpers/warehouse/backend_helper';

// Config
import { warehouse_config } from '../../../../config';

import * as Yup from 'yup';

// ID del tipo de uso de bodega EPP
const EPP_USAGE_TYPE_ID =
	warehouse_config.REACT_APP_WAREHOUSE_USAGE_TYPE_EPP_ID;

export const DestinoSectionSpec = {
	getInitialValues: () => ({
		// EPP
		establishment_destination_id: '',
		warehouse_destination_id: '',
		_warehouse_destination_code: '',

		// EPP Workflow - Campos para Retiro
		pick_person_id: '',

		// EPP Workflow - Campos para Transferencia
		warehouse_origin_id: '',
		_warehouse_origin_code: '',

		// Receptor (debajo del separador)
		receptor_rut: '',
		receptor_name: '',
		receptor_establishment_address: '',
		receptor_establishment_commune: '',
		receptor_giro: '',
		receptor_phone: '',

		// Residuos

		// Campos Libres
		siep_code_destination: '',
	}),
	getValidationSchema: () => {
		const schema = Yup.object({
			// EPP - Solo requerido si guide_category es 'epp'
			establishment_destination_id: Yup.string().required('Requerido'),

			// Bodega destino requerida para retiro y transferencia EPP
			warehouse_destination_id: Yup.string().when(
				['guide_category', 'epp_operation_type'],
				{
					is: (category, operation) =>
						category === 'epp' &&
						(operation === 'retiro' || operation === 'transferencia'),
					then: schema =>
						schema.required('Seleccione bodega EPP de destino'),
					otherwise: schema => schema.notRequired(),
				}
			),

			// Persona que retira requerida para retiro EPP
			pick_person_id: Yup.string().when(
				['guide_category', 'epp_operation_type'],
				{
					is: (category, operation) =>
						category === 'epp' && operation === 'retiro',
					then: schema => schema.required('Seleccione persona que retira'),
					otherwise: schema => schema.notRequired(),
				}
			),

			// Bodega origen requerida para transferencia EPP
			warehouse_origin_id: Yup.string().when(
				['guide_category', 'epp_operation_type'],
				{
					is: (category, operation) =>
						category === 'epp' && operation === 'transferencia',
					then: schema =>
						schema.required('Seleccione bodega fiscal de origen'),
					otherwise: schema => schema.notRequired(),
				}
			),

			// Receptor
			receptor_rut: Yup.string().required('Requerido'),
			receptor_name: Yup.string().required('Requerido'),
			receptor_establishment_address: Yup.string().required('Requerido'),
			receptor_establishment_commune: Yup.string().required('Requerido'),
		});
		return schema;
	},
	toPayload: values => ({
		dtefacele: {
			Encabezado: {
				Receptor: {
					RUTRecep: values.receptor_rut || '',
					RznSocRecep: values.receptor_name || '',
					DirRecep: values.receptor_establishment_address || '',
					CmnaRecep: values.receptor_establishment_commune || '',
					CiudadRecep: values.receptor_establishment_commune || '',
					GiroRecep: values.receptor_giro || '',
					Contacto: values.receptor_phone || '',
				},
				Transporte: {
					DirDest: values.receptor_establishment_address || '',
					CmnaDest: values.receptor_establishment_commune || '',
					CiudadDest: values.receptor_establishment_commune || '',
				},
			},
			CamposLibres: {
				CodigoSiepDestino: values.siep_code_destination || '',
			},
		},
	}),
};

export const DestinoSection = () => {
	const validation = useFormikContext();

	// Estados para datos
	const [selectedDestinyEstablishment, setSelectedDestinyEstablishment] =
		useState(null);
	const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(true);
	const prevEppTypeRef = useRef(validation.values.epp_operation_type);

	// Detectar si es retiro EPP
	const isRetiroEPP =
		validation.values.guide_category === 'epp' &&
		validation.values.epp_operation_type === 'retiro';

	// Detectar si es transferencia EPP
	const isTransferenciaEPP =
		validation.values.guide_category === 'epp' &&
		validation.values.epp_operation_type === 'transferencia';

	const handleDestinyEstablishmentSelected = establishment => {
		setSelectedDestinyEstablishment(establishment);
		validation.setFieldValue(
			'establishment_destination_id',
			establishment?._id || establishment?.value || ''
		);
		// Limpiar bodega destino cuando cambia el establecimiento
		validation.setFieldValue('warehouse_destination_id', '', false);
	};

	// Limpiar campos EPP cuando cambia el tipo de operación (no en mount)
	useEffect(() => {
		const currentType = validation.values.epp_operation_type;
		const prevType = prevEppTypeRef.current;
		prevEppTypeRef.current = currentType;

		// No limpiar si no cambió
		if (prevType === currentType) return;

		if (currentType === 'retiro') {
			validation.setFieldValue('warehouse_origin_id', '', false);
			validation.setFieldValue('_warehouse_origin_code', '', false);
		} else if (currentType === 'transferencia') {
			validation.setFieldValue('pick_person_id', '', false);
		} else if (prevType) {
			// Solo limpiar todo si venimos de un tipo válido (no desde mount)
			validation.setFieldValue('warehouse_destination_id', '', false);
			validation.setFieldValue('pick_person_id', '', false);
			validation.setFieldValue('warehouse_origin_id', '', false);
			validation.setFieldValue('_warehouse_origin_code', '', false);
		}
	}, [validation.values.epp_operation_type]);

	// cargar datos titular del establecimiento de destino
	useEffect(() => {
		if (selectedDestinyEstablishment) {
			// Cargar datos receptor (siempre)
			validation.setFieldValue(
				'receptor_rut',
				selectedDestinyEstablishment?.company?.rut || ''
			);
			validation.setFieldValue(
				'receptor_name',
				selectedDestinyEstablishment?.company?.name || ''
			);
			validation.setFieldValue(
				'receptor_establishment_address',
				selectedDestinyEstablishment?.address || ''
			);
			validation.setFieldValue(
				'receptor_establishment_commune',
				selectedDestinyEstablishment?.commune?.name || ''
			);
			validation.setFieldValue(
				'receptor_giro',
				selectedDestinyEstablishment?.company?.business_activity || ''
			);
			validation.setFieldValue(
				'receptor_phone',
				selectedDestinyEstablishment?.company?.phono || ''
			);
			validation.setFieldValue(
				'siep_code_destination',
				selectedDestinyEstablishment?.code_siep || ''
			);
		}
	}, [selectedDestinyEstablishment]);

	return (
		<Card className='border mb-0'>
			<CardBody className='p-4'>
				<div className='pb-2'>
					<h5 className=''>Información de destino</h5>
				</div>
				<Row className='g-3'>
					{/* Establecimiento Destino */}
					<Col lg={4}>
						<OSelect
							label='Establecimiento de Destino'
							id='establishment_destination_id'
							name='establishment_destination_id'
							isRequired={true}
							value={
								validation.values.establishment_destination_id || ''
							}
							onSelect={handleDestinyEstablishmentSelected}
							requestApi={() =>
								getEstablishmentList({ status_code: 'ACT' })
							}
							validation={validation}
						/>
					</Col>

					{/* Campos para Retiro EPP - Bodega Destino, Persona */}
					{isRetiroEPP && (
						<>
							<Col lg={4}>
								<OSelect
									key={`warehouse_destination_id_${
										validation.values.establishment_destination_id ||
										'none'
									}`}
									label='Bodega EPP de Destino'
									id='warehouse_destination_id'
									name='warehouse_destination_id'
									isRequired={true}
									value={
										validation.values.warehouse_destination_id || ''
									}
									requestApi={() =>
										validation.values.establishment_destination_id
											? getWarehouseList({
													establishment_id:
														validation.values
															.establishment_destination_id,
													warehouse_usage_type_id:
														EPP_USAGE_TYPE_ID,
											  })
											: Promise.resolve([])
									}
									handleLabelOptions={item =>
										`${item?.name || ''} ${
											item?.code_sap ? `(${item.code_sap})` : ''
										}`
									}
									validation={validation}
									disabled={
										!validation.values.establishment_destination_id
									}
									onSelect={warehouse => {
										// Guardar código SAP de bodega destino
										validation.setFieldValue(
											'_warehouse_destination_code',
											warehouse?.code_sap || '',
											false
										);
									}}
									placeholder={
										validation.values.establishment_destination_id
											? 'Seleccione bodega...'
											: 'Primero seleccione establecimiento destino'
									}
								/>
							</Col>
							<Col lg={4}>
								<OSelect
									label='Persona que Retira'
									id='pick_person_id'
									name='pick_person_id'
									isRequired={true}
									value={validation.values.pick_person_id || ''}
									requestApi={() => getPersonList({})}
									handleLabelOptions={item =>
										`${item?.first_name || ''} ${
											item?.first_surname || ''
										} - ${item?.rut || ''}`
									}
									validation={validation}
								/>
							</Col>
						</>
					)}

					{/* Campos para Transferencia EPP - Bodega Destino y Bodega Origen */}
					{isTransferenciaEPP && (
						<>
							<Col lg={4}>
								<OSelect
									key={`warehouse_destination_id_transfer_${
										validation.values.establishment_destination_id ||
										'none'
									}`}
									label='Bodega EPP de Destino'
									id='warehouse_destination_id'
									name='warehouse_destination_id'
									isRequired={true}
									value={
										validation.values.warehouse_destination_id || ''
									}
									requestApi={() =>
										validation.values.establishment_destination_id
											? getWarehouseList({
													establishment_id:
														validation.values
															.establishment_destination_id,
													warehouse_usage_type_id:
														EPP_USAGE_TYPE_ID,
											  })
											: Promise.resolve([])
									}
									handleLabelOptions={item =>
										`${item?.name || ''} ${
											item?.code_sap ? `(${item.code_sap})` : ''
										}`
									}
									validation={validation}
									disabled={
										!validation.values.establishment_destination_id
									}
									onSelect={warehouse => {
										// Guardar código SAP de bodega destino
										validation.setFieldValue(
											'_warehouse_destination_code',
											warehouse?.code_sap || '',
											false
										);
									}}
									placeholder={
										validation.values.establishment_destination_id
											? 'Seleccione bodega EPP...'
											: 'Primero seleccione establecimiento'
									}
								/>
							</Col>
						</>
					)}

					{/* Divider with centered toggle */}
					<Col lg={12} className='p-0'>
						<div className='divider-with-toggle'>
							<hr className='hr' />
							<button
								type='button'
								className='divider-toggle-btn'
								aria-expanded={!isDetailsCollapsed}
								onClick={() => setIsDetailsCollapsed(prev => !prev)}
							>
								<i className='ri-arrow-up-s-line'></i>
							</button>
						</div>
					</Col>
					{/* Campos de autocompletado del Receptor (se envían al API) */}
					<Col lg={12} className='p-0'>
						<div
							className={
								'collapsible-content' +
								(isDetailsCollapsed ? ' is-collapsed' : '')
							}
						>
							<Row className='g-3'>
								<Col lg={4}>
									<OInput
										id='receptor_rut'
										name='receptor_rut'
										type='text'
										label='Rut Titular'
										className='form-control'
										value={validation.values.receptor_rut || ''}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='receptor_name'
										name='receptor_name'
										type='text'
										label='Razón Social Titular'
										className='form-control'
										value={validation.values.receptor_name || ''}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='receptor_giro'
										name='receptor_giro'
										type='text'
										label='Giro Titular'
										className='form-control'
										placeholder='Sin Giro en DB'
										value={validation.values.receptor_giro || ''}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={3}>
									<OInput
										id='receptor_phone'
										name='receptor_phone'
										type='text'
										label='Teléfono Titular'
										className='form-control'
										value={validation.values.receptor_phone || ''}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={5}>
									<OInput
										id='receptor_establishment_address'
										name='receptor_establishment_address'
										label='Dirección Establecimiento'
										type='text'
										className='form-control'
										value={
											validation.values
												.receptor_establishment_address || ''
										}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='receptor_establishment_commune'
										name='receptor_establishment_commune'
										label='Comuna Establecimiento'
										type='text'
										className='form-control'
										value={
											validation.values
												.receptor_establishment_commune || ''
										}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
							</Row>
						</div>
					</Col>
				</Row>
			</CardBody>
		</Card>
	);
};
